// JOGI NYUGTA — Firestore-hozzáférés, EGY helyen (CLAUDE.md 10. szabály).
// Terv: ClickUp 123qmzqzk1r §6.2, §7.3/d.
//
// MIÉRT EZ A MODUL LÉTEZIK: a nyugtát KÉT helyről írjuk —
//   1. a jogi kapu modáljából (App.tsx), amikor a felhasználó nyugtáz
//   2. a REGISZTRÁCIÓBÓL (AuthPage.tsx), mert aki az előző képernyőn
//      elfogadta a feltételeket, ne kapjon rá blokkoló ablakot az
//      onboarding után (§7.3/d)
// Ha a két hely külön állítaná össze a doksi-ID-t vagy a payloadot, egy
// mezőnyi eltérés is azt jelentené, hogy a kapu NEM TALÁLJA MEG a saját
// nyugtáját — és a felhasználó beragadna a blokkoló ablakban. A payload
// alakja a tiszta `legalAckDoc.ts`-ben él, az I/O itt.

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { IS_NATIVE } from "../config/platform";
import { currentBuildInfo } from "../utils/buildInfo";
import { ackDocId, buildAckPayload } from "../utils/legalAckDoc";
import { resolveLegalGate, ackKey, LEGAL_DOC_TYPES, type LegalConfig, type LegalGateItem } from "../utils/legalGate";

/** Az `appConfig/legal` beolvasása. Hibán `null` — FAIL-OPEN (§7.3/a). */
export async function loadLegalConfig(): Promise<LegalConfig> {
  try {
    const snap = await getDoc(doc(db, "appConfig", "legal"));
    return (snap.exists() ? snap.data() : null) as LegalConfig;
  } catch {
    return null;
  }
}

/**
 * A felhasználó nyugtái — CSAK azokra a verziókra, amiket a config NEVEZ MEG.
 *
 * Determinisztikus doksi-ID, tehát `getDoc`-okkal megy: nincs query és nincs
 * index (§6.2). A rules a saját `userId`-re szűrt listát is engedné (2026-10-06-án
 * mérve), de a `getDoc` kevesebbet kér és nem igényel összetett szabályt.
 */
export async function loadAcks(uid: string, config: LegalConfig): Promise<Record<string, boolean>> {
  const acks: Record<string, boolean> = {};
  if (!config) return acks;
  const kerdezett: Array<[string, string]> = [];
  for (const docType of LEGAL_DOC_TYPES) {
    const v = config[docType]?.version;
    if (v) kerdezett.push([docType, v]);
  }
  await Promise.all(
    kerdezett.map(async ([docType, version]) => {
      const snap = await getDoc(doc(db, "legalAcks", ackDocId(uid, docType, version)));
      if (snap.exists()) acks[ackKey(docType, version)] = true;
    })
  );
  return acks;
}

/** Nyugták írása a megadott tételekre. Dobhat — a hívó dönt a kezelésről. */
export async function writeAcks(uid: string, items: LegalGateItem[]): Promise<void> {
  if (items.length === 0) return;
  const build = currentBuildInfo();
  // firestore-full-replace: a nyugta EGÉSZBEN születik, egyszer. Nem részleges
  // frissítés, tehát a 9. szabály merge-követelménye itt nem alkalmazható — és
  // a firestore.rules az `update`-et KIFEJEZETTEN tiltja (append-only), tehát
  // egy merge:true-s írás egy MÁR LÉTEZŐ nyugtán elutasításra futna. A
  // determinisztikus doksi-ID miatt a kétszer megnyomott gomb idempotens.
  await Promise.all(
    items.map((item) =>
      setDoc(
        doc(db, "legalAcks", ackDocId(uid, item.docType, item.version)),
        buildAckPayload({
          uid,
          docType: item.docType,
          version: item.version,
          gate: item.gate,
          surface: IS_NATIVE ? "native" : "web",
          acceptedAt: serverTimestamp(),
          appVersion: build.commit,
        })
      )
    )
  );
}

/**
 * REGISZTRÁCIÓS nyugta (§7.3/d) — azt írja meg, amit a kapu ÉPPEN kérne.
 *
 * ⚠️ A LÉNYEG: nem értelmezi újra, hogy mit kell nyugtázni, hanem lefuttatja
 * ugyanazt a `resolveLegalGate`-et üres nyugta-halmazzal. Így szerkezetileg
 * kizárt, hogy a regisztráció és a kapu MÁST gondoljon — ha itt külön
 * feltételt írnánk (pl. „minden configban szereplő verzió"), akkor egy
 * bejelentési ív közepén regisztráló felhasználó a MÉG NEM HATÁLYOS verziót
 * is lenyugtázná, és a hatálybalépés napján a kapu csendben kimaradna.
 *
 * Következmény, ami SZÁNDÉKOS: aki az ív alatt regisztrál, a hatálybalépés
 * napján megkapja a modált — mert a regisztrációkor a RÉGI szöveg volt
 * hatályban, és azt fogadta el.
 *
 * Hibát NEM dob: a regisztráció nem hasalhat el azon, hogy a nyugta nem
 * íródott meg. Ha elhasal, a kapu később elkéri — késés, nem adatvesztés.
 */
export async function writeRegistrationAcks(uid: string, today: string): Promise<number> {
  try {
    const config = await loadLegalConfig();
    const { gate } = resolveLegalGate(config, {}, today);
    await writeAcks(uid, gate);
    return gate.length;
  } catch {
    return 0;
  }
}
